import { useState, useEffect } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Clock, Percent, User, FileText, ChevronDown, ChevronUp, Send, MessageSquare, Tag, FileSpreadsheet, ArrowUp, ClipboardList, Download } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import { joinPersonName } from '@/utils/names';
import {
  isSeatedWithTeamHead,
  needsChainOverrideConfirm,
  splitPendingBySeat,
  decidedByRoleLabel,
  PENDING_SEGMENT_LABELS,
  type PendingSeatSegment,
  TEAM_HEAD_CHIP_LABEL,
  TEAM_HEAD_OVERRIDE_CONFIRM,
  seatChipLabel,
  canShowEscalate,
  ESCALATE_CONFIRM,
  canApproveDiscountRequest,
  isRepOnlyDiscountRole,
  canDecideDiscountViewer,
} from './discountSeat';
import { approvalDocumentLabel } from './approvalDocumentLabel';
import { DealerRequestBlock } from './DealerRequestBlock';
import { ConvertCreditWarning } from '../dealers/ConvertCreditWarning';
import { WonFollowUpPrompt, readFollowUpDue, type FollowUpDue } from '../admin/WonFollowUpPrompt';
import { readCreditWarning, type CreditWarning } from '../dealers/dealerLedgerApi';
import {
  groupDiscountRequests,
  approveLineLabel,
  approveAllLabel,
  heldHelperText,
  heldHeaderText,
  releasedNoticeText,
  docWordFor,
  documentReleasedForRow,
  REJECT_LINE_LABEL,
} from './discountLineGroups';

// WC-06 — how long `«Doc» released` stays on screen after the approve.
const RELEASE_NOTICE_MS = 6000;

interface ConversationMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: string;
  createdAt?: string;
}

interface DiscountRequest {
  id: string;
  proposalId: string | null;
  salesOrderId: string | null;
  invoiceId: string | null;
  dealId: string | null;
  discountScope: string | null; // 'product' | 'document'
  documentType: string | null; // 'proposal' | 'sales_order' | 'invoice'
  // G4 — the hold path DELETES the sales order, so the id can point at a row
  // that no longer exists while this number survives on the request itself.
  documentNumber?: string | null;
  itemDescription: string | null;
  itemRate: number | null;
  itemQuantity: number | null;
  maxDiscountPct: number | null;
  requestedPct: number;
  requestedAmt: number | null;
  dealValue: number | null;
  dealTitle: string | null;
  customerName: string | null;
  /** 2026-09-26 — dealer behind the document (buyer / on-behalf), server-resolved. */
  dealerName?: string | null;
  reason: string | null;
  status: string;
  // CVG-WEB-1 (L96-1) — approval-chain seat. 'manager' = with team head,
  // 'admin_tier' (legacy rows: 'karan') = with admin; escalatedToRole set =
  // escalated to admin.
  approverTarget?: string | null;
  escalatedToRole?: string | null;
  // CVG2-DQ (L98) — literal role of the decider ('admin' | 'owner' |
  // 'sr_sales_manager' | …). Null on legacy rows decided before the stamp.
  approvedByRole?: string | null;
  remarks: string | null;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  requestedBy: { id: string; firstName: string; lastName: string; email: string };
  approvedBy: { id: string; firstName: string; lastName: string } | null;
  product: { id: string; name: string; sku: string | null; maxDiscount: number | null } | null;
  deal: {
    id: string; title: string; value: number;
    contact: { firstName: string; lastName: string } | null;
    account: { name: string } | null;
    activities: { id: string; title: string; createdAt: string }[];
  } | null;
  conversation?: ConversationMessage[];
  // L232 (Round 6 Wave C, Task 5) — one request per held line. lineIndex /
  // lineCount are 1-based among the document's product-scope requests (null
  // for a whole-order row); `siblings` are the OTHER held lines the caller can
  // see in THIS response; documentDecided / documentTotal count the whole
  // document. Absent on an older backend → the card renders as today.
  lineIndex?: number | null;
  lineCount?: number | null;
  siblings?: Array<{ id: string; itemDescription: string | null; requestedPct: number; approvedDiscountPct: number | null; status: string }>;
  documentDecided?: number | null;
  documentTotal?: number | null;
  // I1 — the server's own `clearDocumentHold` verdict over ALL the document's
  // requests, including rows this caller cannot see. The only thing that may
  // decide whether Convert SO / Download proposal are offered.
  documentReleased?: boolean | null;
  approvedDiscountPct?: number | null;
}

// L232 — what a grouped document adds to the card. Empty for a single-line
// document, and then the card is byte-identical to before L232.
interface CardOpts {
  lineChip?: React.ReactNode;
  helper?: string;
  approveLabel?: string;
  rejectLabel?: string;
  /** Approve-all in flight on this document — every action on the card is disabled. */
  busy?: boolean;
}

// ─── L49 Option B money math (QA3-WEB-ADMIN-1) ───────────────────────────────
// Single source for the KPI strip. All fields nullable-safe:
//  deal amount  = itemRate × qty (product scope) → falls back to dealValue
//  discount ₹   = requestedAmt → falls back to dealAmount × pct/100
//  net total    = dealAmount − discountRs
//  overage      = requestedPct − maxDiscountPct (min 0)
function computeDiscountMoney(req: DiscountRequest) {
  const qty = Number(req.itemQuantity ?? 1) > 0 ? Number(req.itemQuantity ?? 1) : 1;
  const lineTotal = req.itemRate != null ? Number(req.itemRate) * qty : null;
  const dealAmount = lineTotal ?? (req.dealValue != null ? Number(req.dealValue) : null);
  const pct = Number(req.requestedPct);
  const discountRs = req.requestedAmt != null
    ? Number(req.requestedAmt)
    : dealAmount != null && Number.isFinite(pct)
      ? Math.round((dealAmount * pct) / 100)
      : null;
  const netTotal = dealAmount != null && discountRs != null ? dealAmount - discountRs : null;
  const cap = req.maxDiscountPct != null ? Number(req.maxDiscountPct) : null;
  const overagePct = cap != null && Number.isFinite(pct) ? Math.max(0, pct - cap) : null;
  return { dealAmount, discountRs, netTotal, cap, overagePct, pct, qty };
}

// "39" / "7.5" — drop trailing .0 so the hero stays clean.
function fmtPct(p: number): string {
  return Number.isInteger(p) ? String(p) : String(Math.round(p * 10) / 10);
}

export function DiscountRequestsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [requests, setRequests] = useState<DiscountRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('pending');
  // CVG2-DQ (L98) — admin/owner-only segmented toggle over the PENDING list.
  // Default segment = "Waiting for your approval" (rows the admin must act on).
  const [pendingSegment, setPendingSegment] = useState<PendingSeatSegment>('waiting');
  const [scopeFilter, setScopeFilter] = useState<string>('');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [remarksModal, setRemarksModal] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);
  const [remarksText, setRemarksText] = useState('');
  const [expandedConversation, setExpandedConversation] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Record<string, ConversationMessage[]>>({});
  const [newMessage, setNewMessage] = useState<Record<string, string>>({});
  const [sendingMessage, setSendingMessage] = useState<string | null>(null);
  // L232 — per document group: which line is expanded, the Approve-all error,
  // and which group is mid-Approve-all.
  const [expandedByGroup, setExpandedByGroup] = useState<Record<string, string>>({});
  const [groupError, setGroupError] = useState<Record<string, string | null>>({});
  const [groupBusy, setGroupBusy] = useState<string | null>(null);
  // WC-06 — `«Doc» released`, said ONLY when an approve response carried
  // `documentReleased: true`. Never derived from counts: a rejected line is
  // "decided" yet keeps the document held. Lives outside `requests` so the
  // refetch that follows every approve cannot wipe it; clears itself.
  const [releaseNotice, setReleaseNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!releaseNotice) return;
    const t = window.setTimeout(() => setReleaseNotice(null), RELEASE_NOTICE_MS);
    return () => window.clearTimeout(t);
  }, [releaseNotice]);
  const announceRelease = (docNumber: string | null | undefined, documentType: string | null | undefined) => {
    setReleaseNotice(releasedNoticeText(docNumber, docWordFor(documentType)));
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filter) params.status = filter;
      if (scopeFilter) params.discountScope = scopeFilter;
      if (docTypeFilter) params.documentType = docTypeFilter;
      if (customerTypeFilter) params.customerType = customerTypeFilter;
      const res = await api.get('/discount-requests', { params });
      setRequests(res.data.data || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [filter, scopeFilter, docTypeFilter, customerTypeFilter]);

  const loadConversation = async (requestId: string) => {
    try {
      const res = await api.get(`/discount-requests/${requestId}/conversation`);
      setConversationMessages(prev => ({ ...prev, [requestId]: res.data.data || [] }));
    } catch {
      setConversationMessages(prev => ({ ...prev, [requestId]: [] }));
    }
  };

  const toggleConversation = (requestId: string) => {
    if (expandedConversation === requestId) {
      setExpandedConversation(null);
    } else {
      setExpandedConversation(requestId);
      if (!conversationMessages[requestId]) {
        loadConversation(requestId);
      }
    }
  };

  const handleSendMessage = async (requestId: string) => {
    const msg = newMessage[requestId]?.trim();
    if (!msg) return;
    try {
      setSendingMessage(requestId);
      await api.post(`/discount-requests/${requestId}/conversation`, { message: msg });
      setNewMessage(prev => ({ ...prev, [requestId]: '' }));
      loadConversation(requestId);
    } catch {
      alert('Failed to send message');
    } finally {
      setSendingMessage(null);
    }
  };

  // ─── T-017 — Convert SO ────────────────────────────────────────────────
  // 🔒 TWO convert routes exist. This uses the one the web ALREADY converts
  // through (ProposalsPage.tsx:262, ProposalEditorPage.tsx), so the shortcut
  // reuses one path rather than adding a second beside it:
  //   • POST /proposals/:id/convert-to-sales-order  — sales.routes.ts:1514,
  //     hold gate at :1527, refuses a held proposal with 423.   ← this one
  //   • POST /sales-orders/from-proposal/:proposalId — sales-orders.routes.ts:2280,
  //     gate thrown from confirm-sales-order-from-proposal.ts:540, refuses
  //     with 409 at :2558.
  //
  // 🔒 The error is rendered VERBATIM and NOTHING here branches on the status
  // or the code. Both routes answer `PROPOSAL_DISCOUNT_HELD` but with
  // different numbers AND different sentences, so keying on either would break
  // silently against the other route. Passing the server's own words through
  // is correct against both, against a future third, and against a status
  // change — and it tells the rep exactly why. A generic "Failed to convert"
  // would throw away the only actionable part.
  // Rendered inline rather than in an alert() so a test can read it and a
  // rep can copy it.
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [creditWarning, setCreditWarning] = useState<
    { warning: CreditWarning; salesOrderId: string | null } | null
  >(null);
  // 2026-10-07 — "Sale marked Won → Mark that follow-up as done?" before the
  // usual after-convert step (credit warning / open the order).
  const [wonFu, setWonFu] = useState<{ due: FollowUpDue; next: () => void } | null>(null);
  const [convertError, setConvertError] = useState<{ id: string; message: string } | null>(null);

  const handleConvertSo = async (req: DiscountRequest) => {
    if (!req.proposalId || convertingId) return;
    setConvertingId(req.id);
    setConvertError(null);
    setCreditWarning(null);
    try {
      const res = await api.post(`/proposals/${req.proposalId}/convert-to-sales-order`);
      const soId = res.data?.data?.id ?? res.data?.id;
      const due = readFollowUpDue(res.data);
      if (due) {
        // Ask first; the usual after-convert step runs when it closes.
        setWonFu({ due, next: () => afterConvert(res, soId) });
        return;
      }
      afterConvert(res, soId);
    } catch (err: any) {
      setConvertError({
        id: req.id,
        message: err?.response?.data?.error || 'Could not convert this proposal to a sales order.',
      });
    } finally {
      setConvertingId(null);
    }
  };

  const afterConvert = (res: { data: any }, soId: string | undefined) => {
    // 🔒 Dealer credit ledger (LOCKED SPEC 2026-09-23 L6) — the server may
    // attach a credit warning to a conversion it ALREADY PERFORMED. It never
    // refuses, and neither does this screen: when there is a warning we stay
    // put just long enough for the rep to read it, with the created order one
    // click away. No warning ⇒ byte-for-byte today's behaviour.
    const warning = readCreditWarning(res.data);
    if (warning) {
      setCreditWarning({ warning, salesOrderId: soId ?? null });
      return;
    }
    // Never strand the rep on this page: a created order with no id in the
    // response still means the order exists, so send them to the list.
    navigate(soId ? `/sales-orders/${soId}` : '/sales-orders');
  };

  const getDocumentLink = (req: DiscountRequest) => {
    if (req.proposalId) return { label: 'View Proposal', path: `/proposals/${req.proposalId}/edit` };
    if (req.salesOrderId) return { label: 'View Sales Order', path: `/sales-orders/${req.salesOrderId}` };
    if (req.invoiceId) return { label: 'View Invoice', path: `/invoices/${req.invoiceId}` };
    return null;
  };

  // ─── ESC-WEB (2026-08-31, Manak) — pass a request up the chain ─────────────
  // 🔒 The web card has never had this; the mobile inbox always has
  // (approvals_screen.dart:540). A team head who cannot decide a request must
  // be able to send it up rather than Approve something beyond their authority
  // or Reject a deal they would have approved.
  //
  // Confirmed before firing, like the mobile dialog, because it is one-way:
  // once escalated the request is the MD's and this manager can no longer
  // decide it.
  const handleEscalate = async (id: string) => {
    if (!window.confirm(ESCALATE_CONFIRM)) return;
    try {
      setActionLoading(id);
      await api.post(`/discount-requests/${id}/escalate`);
      fetchRequests();
    } catch {
      alert('Failed to escalate request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAction = async (id: string, action: 'approve' | 'reject', remarks?: string) => {
    try {
      setActionLoading(id);
      const res = await api.post(`/discount-requests/${id}/${action}`, { remarks });
      if (action === 'approve' && res?.data?.documentReleased === true) {
        const req = requests.find(r => r.id === id);
        announceRelease(req?.documentNumber, req?.documentType);
      }
      setRemarksModal(null);
      setRemarksText('');
      fetchRequests();
    } catch (err: any) {
      // Show the server's sentence (e.g. "This request is owned by the MD…")
      // instead of a bare "Failed to approve request".
      alert(err?.response?.data?.error || err?.response?.data?.message || `Failed to ${action} request`);
    } finally {
      setActionLoading(null);
    }
  };

  // ─── L232 — Approve all N at requested % ──────────────────────────────────
  // Sequential, in line order, each at ITS OWN requestedPct (the lines differ:
  // 42.5 / 42 / 41). Already-decided rows are never posted. 🔒 STOPS at the
  // first failure and names the line — a partial approval must be visible,
  // not silently skipped over. Refetches either way so the header count and
  // every pill reflect what the server actually did.
  //
  // 🔒 Both guards the single Approve button has apply here too (review HIGH-1):
  //  - a line whose dealer margin is negative has a DISABLED Approve (dealer
  //    billing spec §9.2 — the backend has no such guard, this button is the
  //    net) → refuse the whole batch and name the line, post nothing;
  //  - a line seated with the team head needs the CVG-WEB-1 override confirm
  //    → one confirm for the batch; declined → post nothing.
  //
  // WC-06: the release notice follows the LAST SUCCESSFUL approve response's
  // `documentReleased` — a later failed line does not un-say it.
  const handleApproveAll = async (groupKey: string, pendingRows: DiscountRequest[]) => {
    const blocked = pendingRows.find(r => (r as any).dealerSpread < 0);
    if (blocked) {
      setGroupError(prev => ({ ...prev, [groupKey]: `Line ${blocked.lineIndex ?? '?'} cannot be approved: dealer margin is negative` }));
      return;
    }
    if (pendingRows.some(r => needsChainOverrideConfirm(r, viewerRoleName)) && !window.confirm(TEAM_HEAD_OVERRIDE_CONFIRM)) return;
    setGroupBusy(groupKey);
    setGroupError(prev => ({ ...prev, [groupKey]: null }));
    let released = false;
    try {
      for (const row of pendingRows) {
        try {
          const res = await api.post(`/discount-requests/${row.id}/approve`, { approvedDiscountPct: Number(row.requestedPct) });
          released = res?.data?.documentReleased === true;
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'request failed';
          setGroupError(prev => ({ ...prev, [groupKey]: `Line ${row.lineIndex ?? '?'} failed: ${msg}` }));
          break;
        }
      }
    } finally {
      setGroupBusy(null);
      if (released) {
        const first = pendingRows[0];
        announceRelease(first?.documentNumber, first?.documentType);
      }
      fetchRequests();
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"><Clock size={12} /> Pending</span>;
      case 'approved': return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700"><Check size={12} /> Approved</span>;
      case 'rejected': return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"><X size={12} /> Rejected</span>;
      default: return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">{status}</span>;
    }
  };

  // Card heading: item description for product scope, explicit whole-order
  // label for document scope (the old page showed a bare "Item" there).
  const cardTitle = (req: DiscountRequest) => {
    if (req.itemDescription || req.product?.name) return req.itemDescription || req.product!.name;
    if (req.discountScope === 'document') return 'Whole-order document discount';
    return 'Item';
  };

  // L77 — customer name for the card header. Prefer the backend-populated
  // customerName (BED2 backfill), fall back to the deal contact; null → hide.
  const customerDisplayName = (req: DiscountRequest): string | null => {
    const fromRow = req.customerName?.trim();
    if (fromRow) return fromRow;
    const c = req.deal?.contact;
    const fromContact = joinPersonName(c?.firstName, c?.lastName);
    return fromContact || null;
  };

  // CVG2-DQ (L98) — seat toggle is admin/owner-only, on the pending view.
  // Non-admin viewers (team heads, delegated accounts) see the list unchanged.
  const viewerRoleName = (user?.role?.name ?? '').toLowerCase();
  // SUN-40 D7: super_admin + ceo are the MD too — same queue as admin/owner.
  const isAdminOwner = ['admin', 'owner', 'super_admin', 'ceo'].includes(viewerRoleName);
  // WS-C (parity-r2) — sales reps now reach this page (BlockReps lifted). A rep
  // gets a read-only, own-requests-only view: raise + track status +
  // conversation, but NEVER an Approve/Reject affordance (a rep must never
  // decide their own discount). Same rep-role literals BlockReps uses; the
  // backend list scope (C4) returns own-only for reps — this is the UI gate.
  // SUN-40: the SAME own-only set as the server (REP_ONLY_DISCOUNT_ROLES) —
  // assistant_executive_sales was missing here, so it saw Approve/Reject that 403.
  const isRepRole = isRepOnlyDiscountRole(viewerRoleName);
  // CVG3-DISC (L103-1a) — coordinators get the read/track view: raise + status
  // only, never live Approve/Reject (backend 403 stays the real guard).
  // Reps are read-only too (isRepRole) regardless of canDecideDiscountRole.
  // SUN-40 D7: also read-only for profile P04 by job title (canDecideDiscountViewer).
  const canDecide = canDecideDiscountViewer(user);
  const showSeatToggle = isAdminOwner && filter === 'pending';
  const pendingSegments = splitPendingBySeat(requests);
  const visibleRequests = showSeatToggle
    ? (pendingSegment === 'waiting' ? pendingSegments.waiting : pendingSegments.withManagers)
    : requests;

  // ─── L232 (Round 6 Wave C) — the card, unchanged for a single-line document.
  // `opts` are only ever non-empty when the document holds more than one held
  // line (group.grouped); with no opts the DOM is byte-identical to today
  // (pinned by __tests__/DiscountRequestsPage.nondealer-identical.test.tsx).
  const renderCard = (req: DiscountRequest, opts: CardOpts = {}) => {
        // L49 Option B — KPI-hero flat card with cap bar (per
        // docs/qa-specs/L49-discount-approvals-mockup.html, Manak-picked).
        const m = computeDiscountMoney(req);
        const docLink = getDocumentLink(req);
        const requesterInitial = (req.requestedBy.firstName?.[0] || '?').toUpperCase();
        const custName = customerDisplayName(req);
        // CVG-WEB-1 — pending + seated with team head → chip + override confirm.
        const seatedWithTeamHead = isSeatedWithTeamHead(req);
        // CVG3-DISC (L103-1b) — universal seat chip for non-admin viewers
        // (manager / coordinator / requester). The admin view keeps its L96
        // chip + L98 toggle untouched below.
        const seatChip = !isAdminOwner ? seatChipLabel(req) : null;
        // G4 — Proposal / Sales order / Invoice, or null when we do not know.
        const docLabel = approvalDocumentLabel(req);
    return (
        <div key={req.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_1px_3px_rgba(31,75,58,0.06)]">
          {/* Chips row */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {statusBadge(req.status)}
            {opts.lineChip}
            {/* L96 admin chip (unchanged for admin/owner); CVG3-DISC (L103-1b)
                seat chip for every other viewer — same L96 styling. */}
            {isAdminOwner && seatedWithTeamHead && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                <User size={12} /> {TEAM_HEAD_CHIP_LABEL}
              </span>
            )}
            {seatChip && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                <User size={12} /> {seatChip}
              </span>
            )}
            {req.discountScope && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                req.discountScope === 'product' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
              }`}>
                <Tag size={10} />
                {req.discountScope === 'product' ? 'Product' : 'Document'}
              </span>
            )}
            {/* 🔒 2026-08-30 (G4) — same chip, same styling; only the label
                expression changed. The old inline ternary answered
                "Invoice" for ANY documentType it did not recognise and had
                no fallback for the rows that carry none, so it could put a
                wrong word on an approval card. approvalDocumentLabel is the
                line-for-line port of the phone's Dart helper and returns
                null rather than guessing — no chip beats a wrong chip. */}
            {docLabel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#eef7f2] px-2.5 py-0.5 text-xs font-medium text-[#2c6b53]">
                <FileSpreadsheet size={10} />
                {docLabel}
                {/* 2026-09-15 (Manak): show which document — e.g. "Proposal PROP-90477". */}
                {req.documentNumber ? ` ${req.documentNumber}` : ''}
              </span>
            )}
            <span className="text-xs text-gray-500">· {new Date(req.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </div>

          {/* Heading — qty lives in the heading (L49 decision) */}
          <h3 className="text-base font-bold text-gray-900">
            {cardTitle(req)}
            <span className="ml-2 text-sm font-normal text-gray-500">
              · Qty {m.qty}
              {req.product?.sku ? ` · SKU ${req.product.sku}` : ''}
            </span>
          </h3>

          {/* 2026-09-26 — dealer name on the card (hidden when the document has no dealer) */}
          {req.dealerName && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm">
              <User size={13} className="text-[#E07A16]" />
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Dealer</span>
              <span className="text-[15.5px] font-semibold text-[#1F4B3A]">{req.dealerName}</span>
            </div>
          )}
          {/* L77 — customer name, prominent in the card header (hidden when unknown) */}
          {custName && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm">
              <User size={13} className="text-[#E07A16]" />
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Customer</span>
              {/* CVG2-DQ (L98 polish) — customer name ~15-16px semibold */}
              <span className="text-[15.5px] font-semibold text-[#1F4B3A]">{custName}</span>
              {(req as any).deal?.contact?.customerType && (
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${(req as any).deal.contact.customerType === 'b2b' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                  {(req as any).deal.contact.customerType === 'b2b' ? 'B2B' : 'B2C'}
                </span>
              )}
            </div>
          )}

          {/* KPI stat strip: % hero | deal | disc ₹ | net | actions */}
          <div className="mb-1 mt-3 grid grid-cols-2 items-center gap-x-5 gap-y-3 sm:[grid-template-columns:auto_1px_1fr_1fr_1fr_auto]">
            <div>
              <div className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Discount</div>
              <div className="mt-0.5 text-[26px] font-extrabold leading-none text-red-600 tabular-nums">{fmtPct(m.pct)}%</div>
            </div>
            <div className="hidden h-11 w-px bg-gray-200 sm:block" />
            <div>
              <div className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Deal amount</div>
              <div className="mt-0.5 text-[17px] font-bold text-gray-900 tabular-nums">{m.dealAmount != null ? formatINR(m.dealAmount) : '—'}</div>
            </div>
            <div>
              <div className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Discount ₹</div>
              <div className="mt-0.5 text-[17px] font-bold text-red-600 tabular-nums">{m.discountRs != null ? `−${formatINR(m.discountRs)}` : '—'}</div>
            </div>
            <div>
              <div className="text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Net total</div>
              <div className="mt-0.5 text-[17px] font-bold text-[#1F4B3A] tabular-nums">{m.netTotal != null ? formatINR(m.netTotal) : '—'}</div>
            </div>
            {/* CVG3-DISC (L103-1a) — Approve/Reject render only for decide-
                capable roles; coordinators track status without live buttons. */}
            {req.status === 'pending' && canDecide ? (
              <div className="col-span-2 flex gap-2 sm:col-span-1">
                {canApproveDiscountRequest(req, viewerRoleName) ? (
                  <button
                    onClick={() => {
                      // CVG-WEB-1 — request is seated with the team head; admin
                      // acting now overrides the chain → explicit confirm first.
                      if (needsChainOverrideConfirm(req, viewerRoleName) && !window.confirm(TEAM_HEAD_OVERRIDE_CONFIRM)) return;
                      setRemarksModal({ id: req.id, action: 'approve' }); setRemarksText('');
                    }}
                    disabled={actionLoading === req.id || opts.busy || (req as any).dealerSpread < 0}
                    className="flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-xl bg-green-700 px-4 py-2 text-sm font-bold text-white hover:bg-green-800 disabled:opacity-50"
                  >
                    <Check size={14} />{` ${opts.approveLabel ?? 'Approve'}`}
                  </button>
                ) : null}
                <button
                  onClick={() => {
                    if (needsChainOverrideConfirm(req, viewerRoleName) && !window.confirm(TEAM_HEAD_OVERRIDE_CONFIRM)) return;
                    setRemarksModal({ id: req.id, action: 'reject' }); setRemarksText('');
                  }}
                  disabled={actionLoading === req.id || opts.busy}
                  className="flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <X size={14} />{` ${opts.rejectLabel ?? 'Reject'}`}
                </button>
                {/* ESC-WEB (2026-08-31) — hidden once the request is already
                    with the MD, because the server's decideEscalate is a
                    noop there and the button would promise nothing.
                    🔒 NEVER disabled by dealerSpread: a negative dealer
                    margin must not trap a manager into Approve-or-Reject.
                    Only Approve is ever disabled. */}
                {canShowEscalate(req, viewerRoleName) && (
                  <button
                    onClick={() => handleEscalate(req.id)}
                    disabled={actionLoading === req.id || opts.busy}
                    className="flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                  >
                    <ArrowUp size={14} /> Escalate
                  </button>
                )}
              </div>
            ) : <div className="hidden sm:block" />}
          </div>
          {/* L232 — "Approves this line only. The quote stays held until
              lines 2 and 3 are decided." Only on a multi-line document. */}
          {req.status === 'pending' && canDecide && opts.helper && (
            <div className="mb-1 text-[12.5px] text-gray-600">{opts.helper}</div>
          )}

          {/* Cap bar — requested % vs self-serve cap marker */}
          {m.cap != null && Number.isFinite(m.pct) && (
            <div className="mt-2">
              <div className="relative h-[7px] rounded bg-gray-200/70">
                <div
                  className="h-full rounded bg-gradient-to-r from-[#E07A16] to-red-600"
                  style={{ width: `${Math.min(Math.max(m.pct, 0), 100)}%` }}
                />
                <div
                  className="absolute -bottom-[3px] -top-[3px] w-[2px] bg-[#1F4B3A]"
                  style={{ left: `${Math.min(Math.max(m.cap, 0), 100)}%` }}
                  title={`Self-serve cap ${fmtPct(m.cap)}%`}
                />
              </div>
              <div className="mt-1.5 text-[11.5px] text-gray-600">
                Self-serve cap <b>{fmtPct(m.cap)}%</b> · requested <b>{fmtPct(m.pct)}%</b>
                {m.overagePct != null && m.overagePct > 0 ? (
                  <span className="font-semibold text-red-600"> · {fmtPct(m.overagePct)}% over</span>
                ) : (
                  <span className="font-semibold text-green-700"> · within cap</span>
                )}
              </div>
            </div>
          )}

          {/* Reason — 2026-09-26 (Manak): the gate's machine sentence
              ("Sun Stellar gate-before-generate: SKU …") is never shown on
              the approval page; only a human-typed reason is. */}
          {req.reason && !req.reason.startsWith('Sun Stellar gate-before-generate:') && (
            <div className="mt-2 rounded-r-md border-l-[3px] border-[#E07A16] bg-[#f8faf8] px-2.5 py-1.5 text-xs text-gray-600">
              {req.reason}
            </div>
          )}

          {/* Deal context (customer name lives in the header line above — L77) */}
          {(req.dealTitle || (req.deal?.activities && req.deal.activities.length > 0)) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
              {req.dealTitle && <span>{req.dealTitle}</span>}
              {req.deal?.activities && req.deal.activities.length > 0 && (
                <span className="text-gray-500">
                  Last activity: {req.deal.activities[0].title} — {new Date(req.deal.activities[0].createdAt).toLocaleDateString('en-IN')}
                </span>
              )}
            </div>
          )}

          {/* Requester row */}
          <div className="mt-3 flex items-center gap-2.5">
            <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#1F4B3A] text-sm font-bold text-white">
              {requesterInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-gray-800">
                {req.requestedBy.firstName} {req.requestedBy.lastName}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                <span className="truncate">{req.requestedBy.email}</span>
                {docLink && (
                  <button
                    onClick={() => navigate(docLink.path)}
                    className="inline-flex items-center gap-1 font-medium text-[#E07A16] hover:underline"
                  >
                    <FileText size={11} />
                    {docLink.label}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── T-017 (2026-09-10) — Convert SO + Download proposal ──────────
              🔒 APPROVED ONLY, and the whole strip is absent otherwise —
              not a disabled button, not an empty container. Manak: "when its
              pending approval how can it show all 3 buttons at once." Approve
              and these two never coexist on one card.

              🔒 That gate is not cosmetic. A HELD proposal is refused 423 on
              BOTH actions — convert at sales.routes.ts:1820 ("the irreversible
              billed/GST event"), PDF at index.ts:877.

              🔒 D2 (2026-09-23): the gate used to read `req.status ===
              'approved'` — the REQUEST's status, not the DOCUMENT's. On
              PROP-90543 (line 1 approved, line 2 pending) that rendered two
              guaranteed dead taps, and Download opened a new tab holding a raw
              JSON 423 body. It now mirrors `clearDocumentHold`: zero pending
              AND zero rejected lines, per documentReleasedForRow.

              🔒 proposalId is required: a sales-order-scoped card has nothing
              to convert, and `invoiceId` is deliberately unhandled — no
              destination is invented for it.

              The existing document jump above (:232-234) is untouched; these
              sit beside it. */}
          {documentReleasedForRow(req) && req.proposalId && (
            <div data-testid="t017-actions" className="mt-3 flex flex-wrap items-center gap-2 border-t border-dashed border-gray-200 pt-3">
              <button
                onClick={() => handleConvertSo(req)}
                disabled={convertingId === req.id}
                className="flex min-h-[40px] items-center gap-1.5 whitespace-nowrap rounded-xl bg-[#1F4B3A] px-4 py-2 text-sm font-bold text-white hover:bg-[#173c2e] disabled:opacity-50"
              >
                <ClipboardList size={14} /> Convert SO
              </button>
              <button
                onClick={() => window.open(`/api/v1/proposals/${req.proposalId}/pdf`, '_blank')}
                className="flex min-h-[40px] items-center gap-1.5 whitespace-nowrap rounded-xl border border-[#E07A16] bg-white px-4 py-2 text-sm font-bold text-[#B55A00] hover:bg-[#FDEDD6]"
              >
                <Download size={14} /> Download proposal
              </button>
              {convertError?.id === req.id && (
                /* The server's own sentence, verbatim — a 423 says exactly why. */
                <p className="basis-full text-xs font-medium text-red-600">{convertError.message}</p>
              )}
            </div>
          )}

          {/* Approval / rejection outcome — CVG2-DQ (L98): "(admin)" /
              "(team head)" suffix from approvedByRole; legacy null rows
              omit the suffix. */}
          {req.approvedBy && (req.status === 'approved' || req.status === 'rejected') && (
            <div className="mt-2 flex items-center gap-1.5 text-sm">
              <User size={14} className={req.status === 'approved' ? 'text-green-500' : 'text-red-500'} />
              <span className={req.status === 'approved' ? 'font-medium text-green-700' : 'font-medium text-red-700'}>
                {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.approvedBy.firstName} {req.approvedBy.lastName}
                {decidedByRoleLabel(req.approvedByRole) ? ` (${decidedByRoleLabel(req.approvedByRole)})` : ''}
              </span>
              {req.approvedAt && (
                <span className="text-xs text-gray-500">
                  on {new Date(req.approvedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {req.rejectedAt && (
                <span className="text-xs text-gray-500">
                  on {new Date(req.rejectedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
          {req.remarks && (
            <div className="mt-2 rounded bg-blue-50 px-3 py-1.5 text-sm text-blue-700">
              <span className="font-medium">Remarks:</span> {req.remarks}
            </div>
          )}

          {/* Conversation Thread */}
          <div className="mt-3 border-t border-gray-100 pt-3">
            <button
              onClick={() => toggleConversation(req.id)}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              <MessageSquare size={14} />
              Conversation
              {expandedConversation === req.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {expandedConversation === req.id && (
              <div className="mt-2 space-y-2">
                {(conversationMessages[req.id] || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No messages yet.</p>
                ) : (
                  (conversationMessages[req.id] || []).map((msg, idx) => (
                    <div key={msg.id || idx} className="rounded-lg bg-gray-800/50 px-3 py-2">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-gray-700">{msg.userName}</span>
                        <span className="text-[10px] text-gray-500">{new Date(msg.timestamp || msg.createdAt || '').toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-sm text-gray-600">{msg.message}</p>
                    </div>
                  ))
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newMessage[req.id] || ''}
                    onChange={e => setNewMessage(prev => ({ ...prev, [req.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSendMessage(req.id)}
                    placeholder="Add a message..."
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  />
                  <button
                    onClick={() => handleSendMessage(req.id)}
                    disabled={sendingMessage === req.id || !newMessage[req.id]?.trim()}
                    className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                  >
                    <Send size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
          <DealerRequestBlock request={req as any} />
        </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      {wonFu && (
        <WonFollowUpPrompt
          due={wonFu.due}
          onDone={() => { const next = wonFu.next; setWonFu(null); next(); }}
        />
      )}
      {creditWarning && (
        <ConvertCreditWarning
          warning={creditWarning.warning}
          onOpenOrder={() =>
            navigate(
              creditWarning.salesOrderId
                ? `/sales-orders/${creditWarning.salesOrderId}`
                : '/sales-orders',
            )
          }
          onDismiss={() => setCreditWarning(null)}
        />
      )}
      {releaseNotice && (
        <div role="status" className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-semibold text-green-800">
          {releaseNotice}
        </div>
      )}
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Discount Requests</h1>
            <p className="mt-1 text-sm text-gray-600">
              {isRepRole
                ? 'Track the status of discount requests you’ve raised'
                : 'Review and approve discount requests from your team'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {['pending', 'approved', 'rejected', ''].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-[var(--ink-0)] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Scope filter */}
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-600">Scope:</span>
            {['', 'product', 'document'].map(s => (
              <button
                key={s}
                onClick={() => setScopeFilter(s)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  scopeFilter === s
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {s === '' ? 'All' : s === 'product' ? 'Product' : 'Document'}
              </button>
            ))}
          </div>
          {/* Document type filter */}
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-600">Type:</span>
            {['', 'proposal', 'sales_order', 'invoice'].map(dt => (
              <button
                key={dt}
                onClick={() => setDocTypeFilter(dt)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  docTypeFilter === dt
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {dt === '' ? 'All' : dt === 'proposal' ? 'Proposal' : dt === 'sales_order' ? 'Sales Order' : 'Invoice'}
              </button>
            ))}
          </div>
          {/* Customer type filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Customer:</span>
            {['', 'b2b', 'b2c'].map(ct => (
              <button
                key={ct}
                onClick={() => setCustomerTypeFilter(ct)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  customerTypeFilter === ct
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {ct === '' ? 'All' : ct.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CVG2-DQ (L98) — admin/owner pending-queue seat toggle, counts on BOTH segments */}
      {showSeatToggle && !loading && (
        <div className="mb-4 inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
          {(['waiting', 'with_managers'] as PendingSeatSegment[]).map(seg => {
            const count = seg === 'waiting' ? pendingSegments.waiting.length : pendingSegments.withManagers.length;
            const active = pendingSegment === seg;
            return (
              <button
                key={seg}
                onClick={() => setPendingSegment(seg)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? 'bg-white text-[#1F4B3A] shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {PENDING_SEGMENT_LABELS[seg]}
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${
                  active ? 'bg-[#eef7f2] text-[#2c6b53]' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-500">Loading...</div>
      ) : visibleRequests.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-gray-500">
          <Percent size={48} className="mb-2 opacity-30" />
          <p>
            {showSeatToggle
              ? `No pending requests ${pendingSegment === 'waiting' ? 'waiting for your approval' : 'with managers'}`
              : `No ${filter} discount requests`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupDiscountRequests(visibleRequests).map(group => {
            if (!group.grouped) return group.rows.map(req => renderCard(req));
            // L232 — a multi-line document: header, ONE expanded card, the
            // other lines collapsed, and an Approve-all footer.
            const firstPending = group.rows.find(r => r.status === 'pending') ?? group.rows[0];
            const chosen = expandedByGroup[group.key];
            const expanded = group.rows.find(r => r.id === chosen) ?? firstPending;
            const collapsed = group.rows.filter(r => r.id !== expanded.id);
            const visiblePending = group.rows.filter(r => r.status === 'pending');
            // Approve-all only sends rows this viewer may approve (MD-owned rows 403 for non-Layer-A).
            const approvablePending = visiblePending.filter(r => canApproveDiscountRequest(r, viewerRoleName));
            const lineChipFor = (r: DiscountRequest) => (
              r.lineIndex != null && r.lineCount != null ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
                  Line {r.lineIndex} of {r.lineCount}
                </span>
              ) : null
            );
            const helper = expanded.lineIndex != null && expanded.lineCount != null && expanded.lineCount > 1
              ? heldHelperText(group.docWord, expanded.lineIndex, expanded.lineCount)
              : undefined;
            return (
              <div key={group.key} className="rounded-2xl border border-[#1F4B3A]/25 bg-[#f8faf8] p-2.5 sm:p-3">
                {/* Group header — counts come from the server's documentDecided /
                    documentTotal, never from the rows visible here. */}
                <div data-testid={`dr-group-${group.key}`} className="mb-2 flex flex-wrap items-center justify-between gap-2 px-2 pt-1 text-sm">
                  <div className="text-gray-800">
                    {[
                      group.docNumber ? <span key="n" className="font-bold tabular-nums">{group.docNumber}</span> : null,
                      // 2026-09-26 (Manak) — the dealer is named on the header too; the
                      // customer segment keeps its L232-pinned plain text.
                      group.dealerName ? <span key="d" className="font-semibold text-[#1F4B3A]"><span className="mr-1 text-[10.5px] font-medium uppercase tracking-wide text-gray-600">Dealer</span>{group.dealerName}</span> : null,
                      group.customerName ? <span key="c" className="font-semibold text-[#1F4B3A]">{group.customerName}</span> : null,
                      // WC-06: one neutral pill; no green "released" from counts.
                      <span key="s" className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        {heldHeaderText(group.docWord, group.decided, group.total)}
                      </span>,
                    ].filter(Boolean).map((el, i) => (i === 0 ? el : <span key={`sep-${i}`}>{' · '}{el}</span>))}
                  </div>
                </div>
                {renderCard(expanded, {
                  lineChip: lineChipFor(expanded),
                  helper,
                  approveLabel: approveLineLabel(Number(expanded.requestedPct), expanded.lineCount),
                  rejectLabel: (expanded.lineCount ?? 1) > 1 ? REJECT_LINE_LABEL : undefined,
                  busy: groupBusy === group.key,
                })}
                {collapsed.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {collapsed.map(row => {
                      const rm = computeDiscountMoney(row);
                      const shownPct = row.status === 'approved' && row.approvedDiscountPct != null
                        ? Number(row.approvedDiscountPct)
                        : Number(row.requestedPct);
                      return (
                        <div
                          key={row.id}
                          data-testid={`dr-sibling-${row.id}`}
                          className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5"
                        >
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            {statusBadge(row.status)}
                            {lineChipFor(row)}
                            <span className="font-semibold text-gray-900">{cardTitle(row)}</span>
                            <span className="text-xs text-gray-500">
                              · Qty {rm.qty}
                              {row.product?.sku ? ` · SKU ${row.product.sku}` : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-bold text-red-600 tabular-nums">{fmtPct(shownPct)}%</span>
                            <span className="text-gray-500 tabular-nums">{rm.dealAmount != null ? formatINR(rm.dealAmount) : '—'}</span>
                            <button
                              onClick={() => setExpandedByGroup(prev => ({ ...prev, [group.key]: row.id }))}
                              disabled={groupBusy === group.key}
                              className="text-xs font-semibold text-[#E07A16] hover:underline disabled:opacity-50"
                            >
                              Review ▾
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(visiblePending.length > 0 || groupError[group.key]) && canDecide && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-2 pb-1">
                    <div className="text-xs text-gray-600">
                      {groupError[group.key]
                        ? <span className="font-semibold text-red-600">{groupError[group.key]}</span>
                        : `${visiblePending.length} of ${group.total} ${visiblePending.length === 1 ? 'line' : 'lines'} waiting here`}
                    </div>
                    {approvablePending.length > 0 && (
                      <button
                        onClick={() => handleApproveAll(group.key, approvablePending)}
                        disabled={groupBusy === group.key}
                        className="rounded-xl border border-green-700 bg-white px-4 py-2 text-sm font-bold text-green-800 hover:bg-green-50 disabled:opacity-50"
                      >
                        {groupBusy === group.key ? 'Approving…' : approveAllLabel(approvablePending.length)}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Remarks Modal */}
      {remarksModal && (
        <Modal open onClose={() => setRemarksModal(null)} ariaLabel="Discount request remarks" className="!max-w-md !bg-white p-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {remarksModal.action === 'approve' ? 'Approve' : 'Reject'} Discount Request
            </h3>
            <textarea
              value={remarksText}
              onChange={e => setRemarksText(e.target.value)}
              placeholder="Add remarks (optional)..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              rows={3}
            />
            <div className="mt-4 flex justify-end gap-2">
              <RequestCloseButton
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={() => handleAction(remarksModal.id, remarksModal.action, remarksText || undefined)}
                disabled={actionLoading === remarksModal.id}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
                  remarksModal.action === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {actionLoading === remarksModal.id ? 'Processing...' : remarksModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
